/* Privacy Policy — public legal page covering personal info collection, use,
 * sharing, security, cookies, children's privacy, SMS terms, and contact.
 * Linked from every footer (Home, Systems, Services, Academy). Reuses the
 * canonical Organization JSON-LD via SeoHead so identity signals stay
 * consistent sitewide. */
import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { Link } from 'react-router-dom';
import { Mail, MapPin, MessageSquare } from 'lucide-react';
import { getOrganizationSchema } from '../data/orgSchema';
import { getBreadcrumbSchema } from '../data/breadcrumbSchema';

export default function Privacy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: '/privacy' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path="/privacy"
        title="Privacy Policy — ZHELTA"
        description="How ZHELTA LLC collects, uses, shares, and safeguards your personal information across zhelta.com and our services."
        jsonLd={jsonLd}
      />
      <Header />

      <main className="py-16 md:py-24" data-testid="privacy-page">
        <div className="zh-container max-w-[820px]">
          <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#c9a227]">
            Legal
          </span>

          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Privacy <span className="text-[#f5a623]">Policy</span>
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-[12.5px] text-gray-500">
            <div>
              <span className="uppercase tracking-[0.18em] font-extrabold text-gray-400 mr-1.5">Effective Date</span>
              July 3, 2024
            </div>
            <div>
              <span className="uppercase tracking-[0.18em] font-extrabold text-gray-400 mr-1.5">Last Updated</span>
              June 5, 2025
            </div>
          </div>

          <p className="mt-8 text-[15px] text-gray-600 leading-relaxed">
            At <strong className="text-gray-900">ZHELTA LLC</strong>, we are committed to protecting
            your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you visit our website{' '}
            <a href="https://www.zhelta.com" className="text-[#a8871a] hover:text-[#c9a227] underline underline-offset-2">www.zhelta.com</a>{' '}
            or use our services. Please read this policy carefully. If you do not agree with the
            terms of this policy, please do not access the site or use our services.
          </p>

          <div className="mt-12 space-y-12 text-[15px] text-gray-700 leading-[1.75]">
            <Section testId="privacy-section-collect" title="Information We Collect">
              <h3 className="text-[15px] font-bold text-gray-900 mb-2">Personal Information</h3>
              <p>When you use our services or interact with us, we may collect personal information such as:</p>
              <BulletList>
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Mailing address</li>
                <li>Payment information (credit card details, billing address)</li>
                <li>Account login credentials</li>
                <li>Any other information you voluntarily provide to us</li>
              </BulletList>

              <h3 className="text-[15px] font-bold text-gray-900 mt-7 mb-2">Non-Personal Information</h3>
              <p>We may also collect non-personal information such as:</p>
              <BulletList>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>IP address</li>
                <li>Location data</li>
                <li>Usage data (e.g., pages viewed, time spent on the site)</li>
              </BulletList>
            </Section>

            <Section testId="privacy-section-use" title="How We Use Your Information">
              <p>We use the information we collect for various purposes, including:</p>
              <BulletList>
                <li>To process your transactions and deliver our products and services</li>
                <li>To communicate with you, including responding to inquiries and providing customer support</li>
                <li>To send promotional and marketing communications, where you have consented</li>
                <li>To improve our website, services, and user experience</li>
                <li>To monitor and analyze trends, usage, and activities</li>
                <li>To detect, prevent, and address technical issues and fraudulent activities</li>
                <li>To comply with legal obligations and enforce our terms and policies</li>
              </BulletList>
            </Section>

            <Section testId="privacy-section-share" title="Sharing Your Information">
              <p>We may share your information in the following circumstances:</p>
              <div className="mt-3 space-y-4">
                <SharingItem
                  label="Service Providers"
                  body="We may share your information with third-party service providers to help us operate our business, including payment processors, shipping companies, IT service providers, and marketing agencies."
                />
                <SharingItem
                  label="Legal Compliance"
                  body="We may disclose your information if required by law, regulation, or court order, or if we believe it is necessary to protect the rights, property, or safety of ZHELTA LLC, our customers, or others."
                />
                <SharingItem
                  label="Business Transfers"
                  body="If ZHELTA LLC is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that business transaction."
                />
                <SharingItem
                  label="With Your Consent"
                  body="We may share your information with third parties when you have given us your explicit consent to do so."
                />
              </div>
            </Section>

            <Section testId="privacy-section-rights" title="Your Rights">
              <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
              <BulletList>
                <li>The right to access and receive a copy of your personal information</li>
                <li>The right to rectify or update your personal information</li>
                <li>The right to delete your personal information</li>
                <li>The right to restrict or object to processing of your personal information</li>
                <li>The right to data portability</li>
              </BulletList>
            </Section>

            <Section testId="privacy-section-security" title="Data Security">
              <p>
                We implement appropriate technical and organizational security measures to protect
                your personal information against unauthorized access, alteration, disclosure, or
                destruction. However, no method of transmission over the internet or electronic
                storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </Section>

            <Section testId="privacy-section-cookies" title="Cookies and Tracking Technologies">
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our
                website, analyze site traffic, and understand how you use our services. You can
                control the use of cookies through your browser settings. For more information,
                please refer to our{' '}
                <Link to="/cookies" className="text-[#a8871a] hover:text-[#c9a227] underline underline-offset-2 font-semibold">
                  Cookie Policy
                </Link>.
              </p>
            </Section>

            <Section testId="privacy-section-thirdparty" title="Third-Party Websites">
              <p>
                Our website may contain links to third-party websites. We do not control or endorse
                the content or practices of these websites. We encourage you to review the privacy
                policies of any third-party sites you visit.
              </p>
            </Section>

            <Section testId="privacy-section-children" title="Children's Privacy">
              <p>
                Our services are not intended for individuals under the age of 13. We do not
                knowingly collect personal information from children under 13. If we learn that we
                have collected information from a child under 13, we will take steps to delete that
                information as soon as possible.
              </p>
            </Section>

            <Section testId="privacy-section-changes" title="Changes to This Privacy Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on our website. Your continued use of the
                website or our services after any modifications to the policy will constitute your
                acknowledgment of the modifications and your consent to abide by and be bound by
                the modified policy.
              </p>
            </Section>

            {/* SMS Terms */}
            <Section testId="privacy-section-sms" title="SMS Terms and Conditions">
              <h3 className="text-[15px] font-bold text-gray-900 mb-2">Text &amp; SMS Message Communications</h3>
              <p>
                When booking an appointment online, providing your cell phone number on the contact
                us form, and/or texting ZHELTA LLC unsolicited, you consented to receive text
                messages sent by ZHELTA LLC,{' '}
                <a href="tel:+19147681947" className="font-semibold text-gray-900 hover:text-[#c9a227]">(914) 768-1947</a>,
                regarding inquiries and appointment reminders.
              </p>
              <BulletList>
                <li>Consent to these terms is not a condition of purchase.</li>
                <li>SMS opt-in consent or phone numbers for SMS purposes will <strong>NOT</strong> be shared with third parties and affiliates for marketing purposes.</li>
                <li>Messaging frequency may vary.</li>
                <li>Message and data rates may apply.</li>
                <li>To opt out at any time, text <strong>STOP</strong>.</li>
                <li>For assistance, text <strong>HELP</strong> or visit our website.</li>
              </BulletList>
            </Section>

            {/* Contact */}
            <Section testId="privacy-section-contact" title="Contact Us">
              <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>

              <div className="mt-5 rounded-2xl border border-gray-200 bg-[#FEFBF3] p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#c9a227] flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Email</div>
                    <a
                      href="mailto:legals@zhelta.com"
                      className="text-[15px] font-semibold text-gray-900 hover:text-[#c9a227]"
                      data-testid="privacy-contact-email"
                    >
                      legals@zhelta.com
                    </a>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#c9a227] flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Address</div>
                    <div className="text-[15px] font-semibold text-gray-900">
                      167 Madison Avenue, Suite 205 #1076
                      <br />
                      New York, NY 10016, United States
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Privacy team CTA */}
            <section
              data-testid="privacy-section-cta"
              className="rounded-3xl border border-gray-200 bg-gradient-to-br from-[#0e0e10] to-[#1a1a1d] text-white p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
            >
              <div>
                <h3 className="text-xl sm:text-2xl font-extrabold mb-1">Questions About Your Privacy?</h3>
                <p className="text-[13.5px] text-white/65">
                  Our team is here to address any privacy concerns you may have.
                </p>
              </div>
              <a
                href="mailto:legals@zhelta.com?subject=Privacy%20Inquiry"
                data-testid="privacy-contact-team-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[12.5px] font-bold uppercase tracking-wider bg-[#f5a623] hover:bg-[#e69e1f] text-black transition-transform hover:-translate-y-0.5"
              >
                <MessageSquare className="w-4 h-4" /> Contact Privacy Team
              </a>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* ---------- small presentational helpers (kept inline; simple & local) ---------- */

function Section({ title, testId, children }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function BulletList({ children }) {
  return <ul className="mt-2 list-disc pl-6 space-y-1.5 marker:text-[#f5a623]">{children}</ul>;
}

function SharingItem({ label, body }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
      <div className="text-[13px] font-extrabold text-gray-900 mb-1">{label}</div>
      <p className="text-[14.5px] text-gray-700 m-0">{body}</p>
    </div>
  );
}
